import * as React from 'react'
import { useState, useRef, useEffect } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { Building2, Building, Users, LogOut, User, ChevronRight, Menu, X } from 'lucide-react'
import { useAuth } from '@/features/auth/AuthContext'
import { ROLE_LABELS } from '@/features/auth/types'
import { Avatar } from '@/components/primitives/Avatar'
import './AdminSidebar.css'

const THINKADMIN_NAV = [
  { to: '/admin/organizations', label: 'Organizations', icon: Building },
  { to: '/admin/facilities',    label: 'Facilities',    icon: Building2 },
  { to: '/admin/users',         label: 'Users',         icon: Users },
]

export function AdminSidebar(): React.JSX.Element {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close mobile nav on route change
  useEffect(() => { setMobileOpen(false) }, [location.pathname])

  useEffect(() => {
    if (!menuOpen) return undefined
    function onClick(e: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  async function onLogout(): Promise<void> {
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <>
      {/* Mobile top bar */}
      <div className="admin-mobile-bar">
        <button
          type="button"
          className="admin-mobile-hamburger"
          onClick={() => setMobileOpen(true)}
          aria-label="Open navigation menu"
        >
          <Menu size={20} strokeWidth={1.8} />
        </button>
        <img src="/vitals-logo.svg" alt="eCareLTC Vitals" className="admin-mobile-logo" />
      </div>

      {/* Overlay */}
      {mobileOpen && (
        <div
          className="admin-sidebar-overlay"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

    <aside className={`admin-sidebar${mobileOpen ? ' admin-sidebar-mobile-open' : ''}`}>
      {/* Logo */}
      <div className="admin-sidebar-logo">
        <img src="/vitals-logo.svg" alt="eCareLTC Vitals" className="admin-sidebar-logo-img" />
        <button
          type="button"
          className="admin-sidebar-close-btn"
          onClick={() => setMobileOpen(false)}
          aria-label="Close navigation menu"
        >
          <X size={18} strokeWidth={1.8} />
        </button>
      </div>

      {/* Nav */}
      <nav className="admin-sidebar-nav">
        <p className="admin-sidebar-section-label">Admin</p>
        {THINKADMIN_NAV.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              'admin-sidebar-link' + (isActive ? ' admin-sidebar-link-active' : '')
            }
          >
            <Icon size={16} strokeWidth={1.8} />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* User */}
      {user != null && (
        <div className="admin-sidebar-user-wrap" ref={menuRef}>
          <button
            type="button"
            className="admin-sidebar-user-btn"
            onClick={() => setMenuOpen(v => !v)}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
          >
            <Avatar name={user.name} size="sm" gradient />
            <div className="admin-sidebar-user-info">
              <span className="admin-sidebar-user-name">{user.name}</span>
              <span className="admin-sidebar-user-role">{ROLE_LABELS[user.role]}</span>
            </div>
            <ChevronRight size={13} strokeWidth={2} className={'admin-sidebar-chevron' + (menuOpen ? ' admin-sidebar-chevron-open' : '')} />
          </button>

          {menuOpen && (
            <div className="admin-sidebar-menu" role="menu">
              <button
                type="button"
                className="admin-sidebar-menu-item"
                onClick={() => { setMenuOpen(false); navigate('/profile') }}
              >
                <User size={14} strokeWidth={1.8} />
                Profile
              </button>
              <div className="admin-sidebar-menu-divider" />
              <button
                type="button"
                className="admin-sidebar-menu-item admin-sidebar-menu-danger"
                onClick={() => void onLogout()}
              >
                <LogOut size={14} strokeWidth={1.8} />
                Sign out
              </button>
            </div>
          )}
        </div>
      )}
    </aside>
    </>
  )
}
